import React, { useState, useEffect } from "react";
import "./ProductComment.css";
import { Auth } from "../../../service/utils/auth";

const ProductComment = ({ listingId }) => {
  const { userID } = new Auth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState(null);

  useEffect(() => {
    fetchComments();
  }, [listingId]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://iudi-api.thinkdiff.us/api/listings/${listingId}/comments`
      );

      if (!response.ok) {
        throw new Error("Không thể tải bình luận");
      }

      const data = await response.json();
      setComments(data.comments || []);
    } catch (err) {
      console.error("Lỗi khi tải bình luận:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !userID) return;

    try {
      setSubmitting(true);

      const response = await fetch(
        `https://iudi-api.thinkdiff.us/api/listings/${listingId}/comments`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            UserID: userID,
            CommentText: newComment.trim(),
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Không thể gửi bình luận");
      }

      const result = await response.json();

      // Nếu API trả về comment mới, thêm vào danh sách
      if (result.comment) {
        setComments((prev) => [result.comment, ...prev]);
      } else {
        // Nếu không, tạo mock comment và refresh lại
        await fetchComments();
      }

      setNewComment("");
    } catch (err) {
      console.error("Lỗi khi gửi bình luận:", err);
      alert("Không thể gửi bình luận. Vui lòng thử lại!");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Bạn có chắc muốn xóa bình luận này?")) return;

    try {
      setDeletingCommentId(commentId);

      const response = await fetch(
        `https://iudi-api.thinkdiff.us/api/listings/comments/${commentId}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        throw new Error("Không thể xóa bình luận");
      }

      // Xóa comment khỏi state
      setComments((prev) => prev.filter((c) => c.CommentID !== commentId));
    } catch (err) {
      console.error("Lỗi khi xóa bình luận:", err);
      alert("Không thể xóa bình luận. Vui lòng thử lại!");
    } finally {
      setDeletingCommentId(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) return "Vừa xong";
    if (diffInHours < 24) return `${diffInHours} giờ trước`;
    if (diffInHours < 48) return "Hôm qua";

    return date.toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="comments-container">
        <div className="comments-loading">
          <div className="loading-spinner"></div>
          <p>Đang tải bình luận...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="comments-container">
        <div className="comments-error">
          <p>❌ {error}</p>
          <button onClick={fetchComments} className="retry-btn">
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="comments-container">
      {/* Comment Form */}
      <div className="comment-form-section">
        <h3 className="comment-form-title">💬 Viết bình luận</h3>
        <form onSubmit={handleSubmitComment} className="comment-form">
          <div className="comment-input-wrapper">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Chia sẻ suy nghĩ của bạn về sản phẩm này..."
              className="comment-textarea"
              rows="3"
              disabled={submitting}
            />
          </div>
          <div className="comment-actions">
            <button
              type="submit"
              className="submit-comment-btn"
              disabled={!newComment.trim() || submitting}
            >
              {submitting ? "Đang gửi..." : "Gửi bình luận"}
            </button>
          </div>
        </form>
      </div>

      {/* Comments List */}
      <div className="comments-list-section">
        <div className="comments-header">
          <h3 className="comments-title">💭 Bình luận ({comments.length})</h3>
        </div>

        {comments.length === 0 ? (
          <div className="no-comments">
            <div className="no-comments-icon">💭</div>
            <p>
              Chưa có bình luận nào. Hãy là người đầu tiên chia sẻ suy nghĩ!
            </p>
          </div>
        ) : (
          <div className="comments-list">
            {comments.map((comment) => (
              <div key={comment.CommentID} className="comment-item">
                <div className="comment-avatar">
                  <span>👤</span>
                </div>
                <div className="comment-content">
                  <div className="comment-header">
                    <span className="comment-author">
                      Người dùng #{comment.UserID}
                    </span>
                    <span className="comment-date">
                      {formatDate(comment.CreatedAt)}
                    </span>
                  </div>
                  <div className="comment-text">{comment.CommentText}</div>
                  <div className="comment-actions">
                    <button className="comment-action-btn">👍 Thích</button>
                    <button className="comment-action-btn">💬 Trả lời</button>
                    {/* Hiển thị nút xóa nếu là comment của user hiện tại */}
                    {userID && String(comment.UserID) === String(userID) && (
                      <button
                        className="comment-action-btn delete-btn"
                        onClick={() => handleDeleteComment(comment.CommentID)}
                        disabled={deletingCommentId === comment.CommentID}
                      >
                        {deletingCommentId === comment.CommentID
                          ? "Đang xóa..."
                          : "🗑️ Xóa"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductComment;
